import React, { useState, useRef, useEffect, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Container,
  PlaceholderWrapper,
  ErrorWrapper,
  PlaceholderContainer,
  ErrorContainer,
  ErrorText,
  ActualImage,
} from "./styled";

interface LazyImageProps {
  src: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
  /**
   * Placeholder content to show while image is loading
   */
  placeholder?: React.ReactNode;
  /**
   * Content to show when image fails to load
   */
  errorContent?: React.ReactNode;
  /**
   * Root margin for intersection observer (default: "50px")
   * This determines how far from the viewport the image should start loading
   */
  rootMargin?: string;
  /**
   * Intersection threshold (default: 0.1)
   * This determines what percentage of the image must be visible to trigger loading
   */
  threshold?: number;
  /**
   * Whether to use fade-in animation when image loads
   */
  enableFadeIn?: boolean;
  /**
   * Data test ID for testing purposes
   */
  "data-testid"?: string;
}

interface ImageState {
  isIntersecting: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  hasError: boolean;
}

/**
 * LazyImage component that only loads images when they're visible in the viewport
 *
 * Features:
 * - Intersection Observer API for viewport detection
 * - Loading state with customizable placeholder
 * - Error handling with fallback content
 * - Fade-in animation support
 * - Performance optimized for large lists
 * - Maintains aspect ratio during loading
 *
 * @example
 * ```tsx
 * <LazyImage
 *   src="https://example.com/image.jpg"
 *   alt="Product image"
 *   className="product-image"
 *   enableFadeIn={true}
 *   rootMargin="100px"
 *   onLoad={() => console.log('Image loaded')}
 * />
 * ```
 */
export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt = "",
  className,
  style,
  onLoad,
  onError,
  placeholder,
  errorContent,
  rootMargin = "50px",
  threshold = 0.1,
  enableFadeIn = true,
  "data-testid": dataTestId,
}) => {
  const [imageState, setImageState] = useState<ImageState>({
    isIntersecting: false,
    isLoading: false,
    isLoaded: false,
    hasError: false,
  });

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleImageLoad = useCallback(() => {
    setImageState((prev) => ({
      ...prev,
      isLoading: false,
      isLoaded: true,
      hasError: false,
    }));
    onLoad?.();
  }, [onLoad]);

  const handleImageError = useCallback(() => {
    setImageState((prev) => ({
      ...prev,
      isLoading: false,
      isLoaded: false,
      hasError: true,
    }));
    onError?.();
  }, [onError]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !imageState.isIntersecting) {
          setImageState((prev) => ({
            ...prev,
            isIntersecting: true,
            isLoading: true,
          }));

          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin,
        threshold,
      }
    );

    observerRef.current.observe(container);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [rootMargin, threshold, imageState.isIntersecting]);

  useEffect(() => {
    setImageState({
      isIntersecting: false,
      isLoading: false,
      isLoaded: false,
      hasError: false,
    });
  }, [src]);

  const defaultPlaceholder = (
    <PlaceholderContainer>
      <CircularProgress size={24} sx={{ color: "#ff813a" }} />
    </PlaceholderContainer>
  );

  const defaultErrorContent = (
    <ErrorContainer>
      <ErrorOutlineIcon sx={{ fontSize: 32, color: "#ccc" }} />
      <ErrorText>Failed to load image</ErrorText>
    </ErrorContainer>
  );

  return (
    <Container
      ref={containerRef}
      className={className}
      style={style}
      data-testid={dataTestId}
    >
      {(!imageState.isIntersecting || imageState.isLoading) &&
        !imageState.hasError && (
          <PlaceholderWrapper>
            {placeholder || defaultPlaceholder}
          </PlaceholderWrapper>
        )}

      {imageState.hasError && (
        <ErrorWrapper>{errorContent || defaultErrorContent}</ErrorWrapper>
      )}

      {imageState.isIntersecting && !imageState.hasError && (
        <ActualImage
          ref={imageRef}
          src={src}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          $isLoaded={imageState.isLoaded}
          $enableFadeIn={enableFadeIn}
          style={{
            display: imageState.isLoading ? "none" : "block",
          }}
        />
      )}
    </Container>
  );
};

export default LazyImage;
